import React from "react";
import { useHistory } from "react-router-dom";
import { Button } from "@material-ui/core";
import DataTable from "@/components/DataTable";
import { ReactRender } from "@/components/ReactRender";
import { useTranslation } from "@/langs/useTranslation";
import { getScheduleDeviceListApi } from "@/apis/apiCollection";
import HasPermission from "@/components/commons/HasPermission";
import ScheduleTaskType from "@/containers/Schedule/CreateTemplate/TaskCreator/TaskType";
import ScheduleType from "@/containers/Schedule/CreateTemplate/TaskCreator/ScheduleType";
import TaskTime from "@/containers/Schedule/CreateTemplate/TaskCreator/TaskTime";
import ScheduleTaskRange from "@/containers/Schedule/CreateTemplate/TaskCreator/TaskRange";
import { formatDate } from "./datetimeHandlers";
import { PublishChipStatus } from "@/components/FormFields/PublishStatus";
import { ScheduleChipStatus } from "@/components/FormFields/ScheduleStatus";
import { ExcuteChipStatus } from "@/components/FormFields/ExcuteStatus";

export const ScheduleDeviceListDatatable = React.forwardRef(
  (
    {
      className,
      style,
      getSearchParams,
      footer,
      children,
      onCheckAll,
      onLoaded,
      onDisable,
      onEnable,
      onDelete
    },
    ref
  ) => {
    const { t } = useTranslation();
    const history = useHistory();

    const toDetail = e => {
      e.preventDefault();
      const url = new URL(e.target.href);
      history.push(url.pathname);
    };

    const columns = [
      {
        data: row => {
          return `${row.deviceId},${row.scheduleTemplateId},${row.taskScheduleId}`;
        },
        type: "checkbox"
      },
      {
        name: "deviceSerialNumber",
        data: "deviceSerialNumber",
        title: t({ defaultMessage: "设备SN" }),
        createdCell: (td, data, rowData) => {
          ReactRender(
            <HasPermission permission="Device Monitoring Detail">
              {yes =>
                yes ? (
                  <a
                    href={`/device-list/device-detail/${rowData.deviceId}/${
                      rowData.os_type || "android"
                    }`}
                    onClick={toDetail}
                  >
                    {data}
                  </a>
                ) : (
                  data
                )
              }
            </HasPermission>,
            td
          );
        }
      },
      {
        name: "deviceSerialNumber",
        data: "isPowerOnDevice",
        title: t({ defaultMessage: "是否为代理设备" }),
        orderable: false,
        render: data => {
          return data === 1
            ? t({ defaultMessage: "是" })
            : t({ defaultMessage: "否" });
        }
      },
      {
        name: "deviceSerialNumber",
        data: "deviceName",
        orderable: false,
        title: t({ defaultMessage: "设备名称" })
      },
      {
        name: "deviceSerialNumber",
        data: "deviceDetail",
        orderable: false,
        title: t({ defaultMessage: "描述" })
      },
      // {
      //   name: "deviceSerialNumber",
      //   data: "isProxyDevice",
      //   className: "center",
      //   orderable: false,
      //   title: `${t({ defaultMessage: "是否为代理设备" })}`,
      //   render: data => {
      //     return data
      //       ? t({ defaultMessage: "是" })
      //       : t({ defaultMessage: "否" });
      //   }
      // },
      {
        name: "deviceSerialNumber",
        orderable: false,
        data: row => {
          return row.groups.map(item => item.name).join(", ");
        },
        title: `${t({ defaultMessage: "门店" })}`
      },
      {
        name: "deviceSerialNumber",
        orderable: false,
        data: row => {
          return row.tags.map(item => item.name).join(", ");
        },
        title: t({ defaultMessage: "标签" })
      },
      {
        name: "deviceSerialNumber",
        orderable: false,
        title: `${t({ defaultMessage: "拥有者" })}`,
        data: row => {
          return row.companies.map(item => item.name).join(", ");
        }
      },
      {
        name: "deviceSerialNumber",
        data: "scheduleTemplateName",
        orderable: false,
        title: t({ defaultMessage: "排程模板" })
      },
      {
        data: "taskType",
        orderable: false,
        title: t({ defaultMessage: "排程任务" }),
        createdCell: (td, data, row) => {
          ReactRender(<ScheduleTaskType data={row} />, td);
        }
      },
      {
        data: "taskType",
        title: t({ defaultMessage: "循环模式" }),
        orderable: false,
        createdCell: (td, data, row) => {
          ReactRender(<ScheduleType data={row} />, td);
        }
      },
      {
        data: "taskType",
        orderable: false,
        title: t({ defaultMessage: "执行时间" }),
        createdCell: (td, data, row) => {
          ReactRender(<TaskTime data={row} />, td);
        }
      },
      {
        data: "taskType",
        orderable: false,
        width: 120,
        title: t({ defaultMessage: "执行期间" }),
        createdCell: (td, data, row) => {
          ReactRender(<ScheduleTaskRange data={row} />, td);
        }
      },
      {
        data: "publishStatus",
        orderable: false,
        title: t({ defaultMessage: "发布状态" }),
        className: "center",
        createdCell: (td, data) => {
          ReactRender(<PublishChipStatus status={data} />, td);
        }
      },
      {
        name: "deviceSerialNumber",
        data: "publishDate",
        orderable: false,
        searchable: false,
        title: t({ defaultMessage: "排程发布日期" }),
        render: data => {
          return formatDate(data);
        }
      },
      {
        data: "scheduleStatus",
        orderable: false,
        title: t({ defaultMessage: "排程状态" }),
        className: "center",
        createdCell: (td, data) => {
          ReactRender(<ScheduleChipStatus status={data} />, td);
        }
      },
      {
        name: "deviceSerialNumber",
        data: "lastExecutedDate",
        orderable: false,
        title: t({ defaultMessage: "最后执行时间" }),
        searchable: false,
        render: data => {
          return formatDate(data, "YYYY-MM-DD HH:mm", "-", true);
        }
      },
      // {
      //   data: "lastExecutedStatus",
      //   orderable: false,
      //   title: t({ defaultMessage: "最后执行状态" }),
      //   searchable: false,
      //   createdCell: (td, data) => {
      //     ReactRender(<ExcuteChipStatus status={data} />, td);
      //   }
      // },
      {
        name: "deviceSerialNumber",
        data: "deployUserName",
        orderable: false,
        title: t({ defaultMessage: "部署人" })
      },
      {
        data: null,
        title: t({ defaultMessage: "操作" }),
        // className: "operation2",
        searchable: false,
        orderable: false,
        width: 48,
        createdCell: (td, data, row) => {
          ReactRender(
            <HasPermission permission="schedule_task_list_task_enable_disable">
              {row.scheduleStatus === 0 ? (
                <Button
                  size="small"
                  color="primary"
                  onClick={() => onDisable(row)}
                >
                  {t({ defaultMessage: "关闭" })}
                </Button>
              ) : (
                <Button
                  size="small"
                  color="primary"
                  onClick={() => onEnable(row)}
                >
                  {t({ defaultMessage: "开启" })}
                </Button>
              )}
            </HasPermission>,
            td
          );
        }
      }
      // {
      //   name: "deviceSerialNumber",
      //   data: "deviceSerialNumber",
      //   className: "sticky right operation",
      //   searchable: false,
      //   orderable: false,
      //   width: 48,
      //   createdCell: (td, data, row) => {
      //     ReactRender(
      //       <HasPermission permission="schedule_task_list_task_enable_disable">
      //         <Button
      //           size="small"
      //           color="secondary"
      //           onClick={() => onDelete(row)}
      //         >
      //           {t({ defaultMessage: "删除" })}
      //         </Button>
      //       </HasPermission>,
      //       td
      //     );
      //   }
      // }
    ];

    const ajax = (data, callback) => {
      let orderType;
      let orderWay;
      const order = data.order[0];
      if (order) {
        // orderType = data.columns[order.column]?.name;
        orderType = order.column;
        orderWay = order.dir === "asc" ? 1 : 2;
      }
      return getScheduleDeviceListApi({
        // ...data,
        orderType,
        orderWay,
        ...getSearchParams(),
        page: data.page,
        pageSize: data.pageSize
      }).then(res => {
        callback({
          data: res.scheduleDevices,
          draw: data.draw,
          recordsFiltered: res.total,
          recordsTotal: res.total
        });
      });
    };

    return (
      <DataTable
        ref={ref}
        rowsGroup={["deviceSerialNumber:name"]}
        ordering
        options={{ order: [[1, "asc"]] }}
        columns={columns}
        ajax={ajax}
        className={className}
        style={style}
        footer={footer}
        scrollable
        showCheckAll={false}
        onCheckAll={onCheckAll}
        onLoaded={onLoaded}
      >
        {children}
      </DataTable>
    );
  }
);
